/**
 * Helper utilities to check if Befehl content is incomplete.
 *
 * Aufträge with empty felder are never considered incomplete.
 * Boolean field values are never considered incomplete.
 */

/**
 * Safely checks if a field value is empty.
 * Only applies to string values (input fields) - boolean values are never empty.
 *
 * @param value - The field value to check
 * @returns true if the value is an empty string, false otherwise (including for booleans)
 */
export function isFieldValueEmpty(value: unknown): boolean {
  if (typeof value === 'boolean') return false

  if (typeof value === 'string') {
    return value.trim() === ''
  }

  return value === null || value === undefined
}

/**
 * Safely checks if any field values in a werte object are non-empty.
 * Automatically excludes boolean values from the check.
 *
 * @param werte - Object containing field values
 * @returns true if any string field has content, false otherwise
 */
export function hasNonEmptyStringFields(
  werte: Record<string, unknown>
): boolean {
  return Object.values(werte).some((value) => !isFieldValueEmpty(value))
}

/**
 * Safely checks if any field values in a werte object are empty.
 * Automatically excludes boolean values from the check.
 *
 * @param werte - Object containing field values
 * @returns true if any string field is empty, false otherwise
 */
export function hasEmptyStringFields(werte: Record<string, unknown>): boolean {
  return Object.values(werte).some((value) => {
    if (typeof value === 'boolean') return false
    return isFieldValueEmpty(value)
  })
}

/**
 * Checks if a field has empty werte object (checkboxes don't need warning).
 * Empty werte objects = checkboxes = valid selection, no warning needed.
 *
 * @param feld - The field to check
 * @returns true if field has empty werte object, false otherwise
 */
export function hasEmptyWerte(feld: {
  werte?: Record<string, unknown>
}): boolean {
  if (!feld.werte) return true
  return Object.keys(feld.werte).length === 0
}

/**
 * Get nested value from an object using dot notation.
 *
 * @param obj - The object to search in
 * @param path - Dot-separated path like "zugmeldestelle.name"
 * @returns The value at the path or undefined
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const keys = path.split('.')
  let current: unknown = obj

  for (const key of keys) {
    if (
      current === null ||
      current === undefined ||
      typeof current !== 'object'
    ) {
      return undefined
    }
    current = (current as Record<string, unknown>)[key]
  }

  return current
}

/**
 * Check if a specific field has content.
 * Automatically excludes fields with empty werte objects.
 * Supports nested paths with dot notation.
 *
 * @param auftrag - The auftrag to search in
 * @param kennung - Field kennung like "6.42.1", "1.11.1"
 * @param key - Property key like "km/h", "km", "freitext", or nested like "zugmeldestelle.name"
 * @returns true if field has content, false if empty or has empty werte object
 *
 * @example
 * if (!hasFieldContent(auftrag640, '6.42.1', 'km/h')) {
 *   return true // Field is empty
 * }
 *
 * @example
 * if (!hasFieldContent(auftrag640, '6.43', 'zugmeldestelle.name')) {
 *   return true // Nested field is empty
 * }
 */
export function hasFieldContent(
  auftrag:
    | { felder?: Array<{ kennung: string; werte?: Record<string, unknown> }> }
    | undefined,
  kennung: string,
  key: string
): boolean {
  const feld = auftrag?.felder?.find((f) => f.kennung === kennung)
  if (!feld?.werte) return false

  if (hasEmptyWerte(feld)) return false

  const value = key.includes('.')
    ? getNestedValue(feld.werte, key)
    : feld.werte[key]
  return !isFieldValueEmpty(value)
}

/**
 * Check if field A OR field B has content.
 *
 * @param auftrag - The auftrag to search in
 * @param kennungA - First field kennung
 * @param keyA - First field key
 * @param kennungB - Second field kennung
 * @param keyB - Second field key
 * @returns true if either field has content
 */
export function hasEitherFieldContent(
  auftrag:
    | { felder?: Array<{ kennung: string; werte?: Record<string, unknown> }> }
    | undefined,
  kennungA: string,
  keyA: string,
  kennungB: string,
  keyB: string
): boolean {
  return (
    hasFieldContent(auftrag, kennungA, keyA) ||
    hasFieldContent(auftrag, kennungB, keyB)
  )
}

/**
 * Check if field A AND field B both have content.
 *
 * @param auftrag - The auftrag to search in
 * @param kennungA - First field kennung
 * @param keyA - First field key
 * @param kennungB - Second field kennung
 * @param keyB - Second field key
 * @returns true if both fields have content
 */
export function hasBothFieldsContent(
  auftrag:
    | { felder?: Array<{ kennung: string; werte?: Record<string, unknown> }> }
    | undefined,
  kennungA: string,
  keyA: string,
  kennungB: string,
  keyB: string
): boolean {
  return (
    hasFieldContent(auftrag, kennungA, keyA) &&
    hasFieldContent(auftrag, kennungB, keyB)
  )
}

/**
 * Find auftrag by kennung from befehl.
 *
 * @param befehl - The befehl to search in
 * @param kennung - Auftrag kennung like "1.10", "6.40"
 * @returns auftrag or undefined
 */
export function findAuftrag(
  befehl: {
    auftraege?: Array<{
      kennung: string
      felder?: Array<{ kennung: string; werte?: Record<string, unknown> }>
    }>
  },
  kennung: string
) {
  return befehl.auftraege?.find((a) => a.kennung === kennung)
}

/**
 * Check if field exists (for discriminator logic).
 *
 * @param auftrag - The auftrag to search in
 * @param kennung - Field kennung
 * @returns true if field exists
 */
export function hasField(
  auftrag:
    | { felder?: Array<{ kennung: string; werte?: Record<string, unknown> }> }
    | undefined,
  kennung: string
): boolean {
  return auftrag?.felder?.some((f) => f.kennung === kennung) || false
}

export function hasEitherField(
  auftrag:
    | { felder?: Array<{ kennung: string; werte?: Record<string, unknown> }> }
    | undefined,
  kennungA: string,
  kennungB: string
): boolean {
  return hasField(auftrag, kennungA) || hasField(auftrag, kennungB)
}

/**
 * Either/or validation: field A or both fields B1+B2 must have content.
 *
 * @param auftrag - The auftrag to search in
 * @param kennungA - First option field kennung
 * @param keyA - First option field key
 * @param kennungB1 - Second option first field kennung
 * @param keyB1 - Second option first field key
 * @param kennungB2 - Second option second field kennung
 * @param keyB2 - Second option second field key
 * @returns true if option A has content OR both option B fields have content
 */
export function hasFieldOrBothFields(
  auftrag:
    | { felder?: Array<{ kennung: string; werte?: Record<string, unknown> }> }
    | undefined,
  kennungA: string,
  keyA: string,
  kennungB1: string,
  keyB1: string,
  kennungB2: string,
  keyB2: string
): boolean {
  const hasA = hasFieldContent(auftrag, kennungA, keyA)
  const hasBoth = hasBothFieldsContent(
    auftrag,
    kennungB1,
    keyB1,
    kennungB2,
    keyB2
  )
  return hasA || hasBoth
}

export function hasMissingWertOrEmptyZugmeldestelle(
  auftrag:
    | { felder?: Array<{ kennung: string; werte?: Record<string, unknown> }> }
    | undefined,
  kennung: string
) {
  if (auftrag) {
    if (hasField(auftrag, kennung)) {
      if (!hasFieldContent(auftrag, kennung, 'zugmeldestelle.art')) {
        return true
      }
      if (!hasFieldContent(auftrag, kennung, 'zugmeldestelle.name')) {
        return true
      }
    }
  }
  return false
}

export function hasMissingWertOrEmptyKmField(
  auftrag:
    | { felder?: Array<{ kennung: string; werte?: Record<string, unknown> }> }
    | undefined,
  kennung: string
) {
  if (auftrag) {
    if (hasField(auftrag, kennung)) {
      if (!hasFieldContent(auftrag, kennung, 'km')) {
        return true
      }
    }
  }
  return false
}

export function hasMissingWertOrEmptyKmhField(
  auftrag:
    | { felder?: Array<{ kennung: string; werte?: Record<string, unknown> }> }
    | undefined,
  kennung: string
) {
  if (auftrag) {
    if (hasField(auftrag, kennung)) {
      if (!hasFieldContent(auftrag, kennung, 'kmh')) {
        return true
      }
    }
  }
  return false
}

export function hasMissingWertOrEmptySignalField(
  auftrag:
    | { felder?: Array<{ kennung: string; werte?: Record<string, unknown> }> }
    | undefined,
  kennung: string
) {
  if (auftrag) {
    if (hasField(auftrag, kennung)) {
      if (!hasFieldContent(auftrag, kennung, 'signal')) {
        return true
      }
    }
  }
  return false
}

export function hasMissingWertOrEmptyFreitextField(
  auftrag:
    | { felder?: Array<{ kennung: string; werte?: Record<string, unknown> }> }
    | undefined,
  kennung: string
) {
  if (auftrag) {
    if (hasField(auftrag, kennung)) {
      if (!hasFieldContent(auftrag, kennung, 'freitext')) {
        return true
      }
    }
  }
  return false
}
